import {useTranslation} from 'react-i18next';
import {SEPSIS_TOOL_TYPE} from '../../forms/formsUtil';
import React, {Fragment, useEffect, useMemo, useState} from 'react';
import _ from 'lodash';
import {checkIfSectionSelected} from '../sepsisUtil';
import {mapObservationFromAPI} from '../../forms/observation/obsUtils';
import {Checkbox, Divider, FormControl, FormControlLabel, FormGroup, Grid, RadioGroup} from '@mui/material';
import Typography from '@mui/material/Typography';
import CheckButton from '../checkButton';
import clsx from 'clsx';
import Radio from '@mui/material/Radio';
import Paper from '@mui/material/Paper';
import {useSepsisStyles} from '../sepsisToolForm';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import InformationBox from '../../../../components/other/informationBox';

const AdultSepsisTool = (props) => {
    const classes = useSepsisStyles();
    const {t} = useTranslation();

    const checkReadOnly = () => {
        const {data} = props;

        if (data) {
            switch (data.status) {
                case SEPSIS_TOOL_TYPE.NEW:
                case SEPSIS_TOOL_TYPE.IN_COMPLETED:
                    return false;
                default: {
                    return true;
                }
            }
        }
    };

    // Helper const
    const isReadOnly = checkReadOnly();

    // Observation
    const [newOBS, setNewOBS] = useState(false);

    // Data Form State
    const [form, setForm] = useState({
        infection: {
            sourceUnclear: false,
            pneumonia: false,
            urinaryInfection: false,
            painOrDistension: false,
            arthritisOrInfection: false,
            deviceInfection: false,
            meningitis: false,
            other: false,
            otherSpecify: '',
        },
        redFlag: {
            systolicBP: false,
            lactate: false,
            respRate: false,
            heartRate: false,
            responds: false,
            needOxygen: false,
            skinIssues: false,
            urine: false,
            chemotherapy: false,
        },
        moderateRiskCriteria: {
            alteredMentalState: false,
            deteriorationFunctionalAbility: false,
            rigors: false,
            immunosuppressed: false,
            traumaSurgery: false,
            woundDeviceSkinInfection: false,
            respRateHardBreath: false,
            heartRateOrArrhythmia: false,
            systolic: false,
            notPassedUrine: false,
            temperature: false,
        },
        sendBloods: {
            by: '',
            date: null,
            check: false,
        },
        doctorReview: {
            by: '',
            date: null,
            check: false,
        },
        clinicianAttended: {
            by: '',
            date: null,
            check: false,
        },
        antimicrobial: {
            by: '',
            date: null,
            check: false,
        },
        aki: false,
        status: SEPSIS_TOOL_TYPE.NEW,
    });

    useEffect(() => {
        let cancel = false;
        if (_.isEmpty(props.data) || props.data.status === SEPSIS_TOOL_TYPE.NEW || props.data.status === SEPSIS_TOOL_TYPE.IN_COMPLETED) {
            if (props.patient?.patient?.lastObservation) {
                checkObsFlag(props.patient?.patient?.lastObservation);
                if (!_.isEmpty(props.data)) {
                    setNewOBS(props.data.obsUsed !== props.patient.patient.lastObservation.examinationStart);
                }
            }
        }

        // Get selected sepsis
        if (!_.isEmpty(props.data)) {
            setForm(prevState => ({
                ...prevState,
                ...props.data,
            }));
        }

        return () => {
            cancel = true;
        };
    }, []);

    useEffect(() => {
        const buttons = form.clinicianAttended.check && form.sendBloods.check && form.doctorReview.check;
        props.save(createObject());
        let draft = checkIfSectionSelected(form.moderateRiskCriteria) && !buttons;

        props.lowRisk(!checkIfSectionSelected(form.moderateRiskCriteria) && !checkIfSectionSelected(form.redFlag));
        props.moderateRisk(checkIfSectionSelected(form.moderateRiskCriteria) && !checkIfSectionSelected(form.redFlag));
        if (buttons) {
            draft = !form.aki || form.aki === 'no';
        }

        if (form.antimicrobial.check) {
            draft = false;
        }

        props.draft(draft);
    }, [form]);

    useEffect(() => {
        const check = !form.sendBloods.check && !form.doctorReview.check && !form.clinicianAttended.check;
        if (check && _.isEmpty(props.data)) {
            setForm(prevState => ({
                ...prevState,
                aki: null,
                antimicrobial: {
                    by: '',
                    date: null,
                    check: false,
                },
            }));
        }
    }, [form.sendBloods, form.doctorReview, form.clinicianAttended]);

    const createObject = () => {
        return {
            type: 'adult',
            ...form,
        };
    };

    const clearOtherOption = () => {
        const clearData = {
            moderateRiskCriteria: {
                alteredMentalState: false,
                deteriorationFunctionalAbility: false,
                rigors: false,
                immunosuppressed: false,
                traumaSurgery: false,
                woundDeviceSkinInfection: false,
                respRateHardBreath: false,
                heartRateOrArrhythmia: false,
                systolic: false,
                notPassedUrine: false,
                temperature: false,
            },
            sendBloods: {
                by: '',
                date: null,
                check: false,
            },
            doctorReview: {
                by: '',
                date: null,
                check: false,
            },
            clinicianAttended: {
                by: '',
                date: null,
                check: false,
            },
            antimicrobial: {
                by: '',
                date: null,
                check: false,
            },
            aki: false,
        };

        setForm(prevState => ({...prevState, ...clearData}));
    };

    // TODO "Systolic BP =< 90 mmHg (or drop >40 from normal)" what is normal??
    const checkObsFlag = (obs) => {
        if (form.status === SEPSIS_TOOL_TYPE.NEW && _.isEmpty(props.data)) {
            if (obs && obs.tests) {
                const mappedObs = mapObservationFromAPI(obs, true);
                let respiration = mappedObs.respiration;
                let consciousness = mappedObs.consciousness;
                let bloodPressure = mappedObs.bloodPressure;
                let pulseRate = mappedObs.pulseRate;

                // Check test status, if not completed then set default value
                respiration = respiration?.status === 'completed' ? respiration?.breaths : null;
                consciousness = consciousness?.status === 'completed' ? consciousness?.consciousnessLevel?.id : null;
                bloodPressure = bloodPressure?.status === 'completed' ? bloodPressure?.systolicPressure : null;
                pulseRate = pulseRate?.status === 'completed' ? pulseRate?.pulseRate : null;

                const dataSet = {
                    systolicBP: !bloodPressure ? false : bloodPressure <= 90,
                    respRate: !respiration ? false : respiration >= 25,
                    heartRate: !pulseRate ? false : pulseRate >= 130,
                    responds: !consciousness ? false : consciousness === 'respondsToVoice' || consciousness === 'respondsToPain' || consciousness === 'unresponsive',
                };

                setForm(prevState => ({
                    ...prevState,
                    obsUsed: obs.examinationStart,
                    redFlag: {...prevState.redFlag, ...dataSet},
                }));
            } else {
                setForm(prevState => ({
                    ...prevState,
                    obsUsed: null,
                }));
            }
        }
    };

    const checkboxHandler = (event, section) => {
        const {name, checked} = event.target;

        // eslint-disable-next-line default-case
        switch (section) {
            case 'infection': {
                setForm(prevState => ({...prevState, infection: {...prevState.infection, [name]: checked}}));
                break;
            }
            case 'redFlag': {
                clearOtherOption();
                setForm(prevState => ({...prevState, redFlag: {...prevState.redFlag, [name]: checked}}));
                break;
            }
            case 'moderateRiskCriteria': {

                setForm(prevState => ({
                    ...prevState,
                    moderateRiskCriteria: {...prevState.moderateRiskCriteria, [name]: checked},
                }));
                break;
            }
        }
    };

    const buttonHandler = (event) => {
        const {name, value} = event;
        if (!form.clinicianAttended.check || !form.sendBloods.check || !form.doctorReview.check) {
            setForm(prevState => ({...prevState, aki: false}));
        }

        setForm(prevState => ({...prevState, [name]: {...value}}));
    };

    const handleAki = (event) => {
        if (event.target.value === 'yes') {
            setForm(prevState => ({
                ...prevState, aki: event.target.value, antimicrobial: {
                    by: '',
                    date: null,
                    check: false,
                },
            }));
        } else {
            setForm(prevState => ({...prevState, aki: event.target.value}));
        }
    };

    // Render sections
    const section1 = useMemo(() => {
        return (
            <>
                <FormControl variant="outlined" style={{width: '100%'}}>
                    <FormGroup>
                        <Grid
                            container
                            direction="row"
                            spacing={2}
                            justifyContent="space-between"
                            alignItems="flex-start"
                        >
                            <Grid item xs={12} md={6}>
                                <Grid container direction="column" justifyContent="center" alignItems="flex-start">
                                    <FormControlLabel
                                        label={
                                            <Typography
                                                variant="body2">
                                                {t('Yes, but source unclear at present')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.infection.sourceUnclear}
                                                onChange={event => checkboxHandler(event, 'infection')}
                                                name="sourceUnclear"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Pneumonia')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={<Checkbox
                                            checked={form.infection.pneumonia}
                                            onChange={event => checkboxHandler(event, 'infection')}
                                            name="pneumonia"
                                            disabled={isReadOnly}
                                        />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Urinary Tract Infection')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={<Checkbox
                                            checked={form.infection.urinaryInfection}
                                            onChange={event => checkboxHandler(event, 'infection')}
                                            name="urinaryInfection"
                                            disabled={isReadOnly}
                                        />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography
                                                variant="body2">
                                                {t('Abdominal pain or distension')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={<Checkbox
                                            checked={form.infection.painOrDistension}
                                            onChange={event => checkboxHandler(event, 'infection')}
                                            name="painOrDistension"
                                            disabled={isReadOnly}
                                        />
                                        }
                                    />
                                </Grid>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Grid container direction="column" justifyContent="center" alignItems="flex-end">
                                    <FormControlLabel
                                        label={
                                            <Typography
                                                variant="body2">
                                                {t('Cellulitis / septic arthritis / infected wound')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.infection.arthritisOrInfection}
                                                onChange={event => checkboxHandler(event, 'infection')}
                                                name="arthritisOrInfection"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Device-related infection')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.infection.deviceInfection}
                                                onChange={event => checkboxHandler(event, 'infection')}
                                                name="deviceInfection"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Meningitis')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.infection.meningitis}
                                                onChange={event => checkboxHandler(event, 'infection')}
                                                name="meningitis"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Other (specify)')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.infection.other}
                                                onChange={event => checkboxHandler(event, 'infection')}
                                                name="other"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </FormGroup>
                </FormControl>

                {form.infection.other &&
                    <Grid item xs={12} style={{paddingTop: '10px'}}>
                        <AgyleTextField
                            name="otherSpecify"
                            label={t('Specify')}
                            onChange={event => setForm((prevState => ({
                                ...prevState,
                                infection: {...prevState.infection, otherSpecify: event.target.value},
                            })))}
                            value={form.infection.otherSpecify || ''}
                            required
                            error={form.infection.otherSpecify === ''}
                            inputProps={
                                {readOnly: isReadOnly}
                            }
                            fullWidth
                            size="large"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>
                }
            </>
        );
    }, [form.infection, isReadOnly]);

    const section2 = useMemo(() => {
        return (
            <>
                <FormControl variant="outlined" style={{width: '100%'}}>
                    <FormGroup>
                        <Grid
                            container
                            direction="row"
                            spacing={2}
                            justifyContent="space-between"
                            alignItems="flex-start"
                        >
                            <Grid item xs={12} md={6}>
                                <Grid container direction="column" justifyContent="center" alignItems="flex-start">
                                    <FormControlLabel
                                        label={
                                            <Typography
                                                variant="body2">
                                                {t('Systolic BP =< 90 mmHg (or drop >40 from normal)')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={<Checkbox checked={form.redFlag.systolicBP} disabled={true}/>}
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Lactate >= 2 mmol/L')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={<Checkbox
                                            checked={form.redFlag.lactate}
                                            onChange={event => checkboxHandler(event, 'redFlag')}
                                            name="lactate"
                                            disabled={isReadOnly}
                                        />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Respiratory Rate (RR) >= 25 per minute')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={<Checkbox checked={form.redFlag.respRate} disabled={true}/>}
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Heart Rate (HR) >= 130 per minute')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={<Checkbox checked={form.redFlag.heartRate} disabled={true}/>}
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Responds only to voice, pain or is unresponsive')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={<Checkbox checked={form.redFlag.responds} disabled={true}/>}
                                    />
                                </Grid>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Grid container direction="column" justifyContent="center" alignItems="flex-end">
                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Needs oxygen to keep Sp02 >= 92% (88% in COPD)')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.redFlag.needOxygen}
                                                onChange={event => checkboxHandler(event, 'redFlag')}
                                                name="needOxygen"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Non-blanching rash, mottled / ashen / cyanotic')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.redFlag.skinIssues}
                                                onChange={event => checkboxHandler(event, 'redFlag')}
                                                name="skinIssues"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Urine output less than 0.5 ml/kg/hr or no UO in last 18 hrs')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.redFlag.urine}
                                                onChange={event => checkboxHandler(event, 'redFlag')} name="urine"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Chemotherapy in last 6/52 or haematological disorder causing neutropenia')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.redFlag.chemotherapy}
                                                onChange={event => checkboxHandler(event, 'redFlag')}
                                                name="chemotherapy"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </FormGroup>
                </FormControl>
            </>
        );
    }, [form.redFlag, isReadOnly]);

    const section3 = useMemo(() => {
        return (
            <>
                <FormControl variant="outlined" style={{width: '100%'}}>
                    <FormGroup>
                        <Grid
                            container
                            direction="row"
                            spacing={2}
                            justifyContent="space-between"
                            alignItems="flex-start"
                        >
                            <Grid item xs={12} md={6}>
                                <Grid container direction="column" justifyContent="center" alignItems="flex-start">
                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Relatives concerned about altered mental state')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.moderateRiskCriteria.alteredMentalState}
                                                onChange={event => checkboxHandler(event, 'moderateRiskCriteria')}
                                                name="alteredMentalState"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Acute deterioration in functional ability')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.moderateRiskCriteria.deteriorationFunctionalAbility}
                                                onChange={event => checkboxHandler(event, 'moderateRiskCriteria')}
                                                name="deteriorationFunctionalAbility"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Rigors')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.moderateRiskCriteria.rigors}
                                                onChange={event => checkboxHandler(event, 'moderateRiskCriteria')}
                                                name="rigors"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Immunosuppressed')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.moderateRiskCriteria.immunosuppressed}
                                                onChange={event => checkboxHandler(event, 'moderateRiskCriteria')}
                                                name="immunosuppressed"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Trauma, surgery or procedure in last 6 weeks')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.moderateRiskCriteria.traumaSurgery}
                                                onChange={event => checkboxHandler(event, 'moderateRiskCriteria')}
                                                name="traumaSurgery"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Clinical signs of wound, device or skin infection')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.moderateRiskCriteria.woundDeviceSkinInfection}
                                                onChange={event => checkboxHandler(event, 'moderateRiskCriteria')}
                                                name="woundDeviceSkinInfection"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />
                                </Grid>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Grid container direction="column" justifyContent="center" alignItems="flex-end">
                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Respiratory rate 21-24 OR breathing hard')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.moderateRiskCriteria.respRateHardBreath}
                                                onChange={event => checkboxHandler(event, 'moderateRiskCriteria')}
                                                name="respRateHardBreath"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Heart rate 91-130 OR new arrhythmia')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.moderateRiskCriteria.heartRateOrArrhythmia}
                                                onChange={event => checkboxHandler(event, 'moderateRiskCriteria')}
                                                name="heartRateOrArrhythmia"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Systolic BP 91-100 mmHg')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.moderateRiskCriteria.systolic}
                                                onChange={event => checkboxHandler(event, 'moderateRiskCriteria')}
                                                name="systolic"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Not passed urine for 12-18 hrs')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.moderateRiskCriteria.notPassedUrine}
                                                onChange={event => checkboxHandler(event, 'moderateRiskCriteria')}
                                                name="notPassedUrine"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        label={
                                            <Typography variant="body2">
                                                {t('Temperature <36C')}
                                            </Typography>
                                        }
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                checked={form.moderateRiskCriteria.temperature}
                                                onChange={event => checkboxHandler(event, 'moderateRiskCriteria')}
                                                name="temperature"
                                                disabled={isReadOnly}
                                            />
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </FormGroup>
                </FormControl>
            </>
        );
    }, [form.moderateRiskCriteria, isReadOnly]);

    const section4 = useMemo(() => {
        return (
            <>
                <Grid container spacing={2} style={{width: '100%'}}>
                    {/* Section 4 */}
                    <Grid item xs={12} md={6}>
                        <div className={classes.sectionBoxAdult}>
                            <span
                                className={classes.smallFont}
                                style={{fontWeight: 'bold'}}
                            >
                                {t('Send bloods')}
                            </span>

                            <br/>

                            <span className={classes.smallFont}>
                                {t('to include FBC, U&Es, CRP, LFTs, clotting')}
                            </span>
                        </div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <div className={classes.sectionBoxAdult}>
                            <CheckButton
                                data={form.sendBloods}
                                onClick={buttonHandler}
                                name="sendBloods"
                                disabled={isReadOnly}
                            />
                        </div>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider/>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <div className={classes.sectionBoxAdult}>
                            <span
                                className={classes.smallFont}
                                style={{fontWeight: 'bold'}}
                            >
                                {t('Contact senior doctor to review')}
                            </span>

                            <br/>

                            <span className={clsx(classes.smallFont, 'red')}>
                                {t('USE SBAR! must review results within 1 hour')}
                            </span>
                        </div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <div className={classes.sectionBoxAdult}>
                            <CheckButton
                                data={form.doctorReview}
                                onClick={buttonHandler}
                                name="doctorReview"
                                disabled={isReadOnly}
                            />
                        </div>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider/>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <div className={classes.sectionBoxAdult}>
                            <span
                                className={classes.smallFont}
                                style={{fontWeight: 'bold'}}
                            >
                                {t('Has clinician attended?')}
                            </span>
                        </div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <div className={classes.sectionBoxAdult}>
                            <CheckButton
                                data={form.clinicianAttended}
                                onClick={buttonHandler}
                                name="clinicianAttended"
                                disabled={isReadOnly}
                            />
                        </div>
                    </Grid>

                    {/* Section 5 */}
                    {checkIfSectionSelected(form, true) &&
                        <>
                            <Grid item xs={12}>
                                <Divider className={classes.divider}/>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <div className={classes.sectionBox}>
                                    <span
                                        className={classes.smallFont}
                                        style={{fontWeight: 'bold'}}
                                    >
                                        {t('Is AKI present?')}
                                    </span>
                                </div>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <div className={classes.sectionBox} style={{textAlign: 'center'}}>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            value={form.aki}
                                            onChange={handleAki}
                                            row
                                        >
                                            <FormControlLabel
                                                disabled={isReadOnly}
                                                value="yes"
                                                control={<Radio/>}
                                                label={t('YES')}
                                            />

                                            <FormControlLabel
                                                disabled={isReadOnly}
                                                value="no"
                                                control={<Radio/>}
                                                label={t('NO')}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </div>
                            </Grid>
                        </>
                    }

                    {/* Section 6 */}
                    {form.aki === 'no' &&
                        <>
                            <Grid item xs={12}>
                                <Divider className={classes.divider}/>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <div className={classes.sectionBoxAdult}>
                                    <span
                                        className={classes.smallFont}
                                        style={{fontWeight: 'bold'}}
                                    >
                                        {t('Clinician to make antimicrobial')}
                                    </span>

                                    <br/>

                                    <span
                                        className={classes.smallFont}
                                        style={{fontWeight: 'bold'}}
                                    >
                                        {t('Prescribing decision within 3 hrs')}
                                    </span>
                                </div>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <div className={classes.sectionBoxAdult}>
                                    <CheckButton
                                        data={form.antimicrobial}
                                        onClick={buttonHandler}
                                        name="antimicrobial"
                                        disabled={isReadOnly}
                                    />
                                </div>
                            </Grid>
                        </>
                    }
                </Grid>
            </>
        );
    }, [form, isReadOnly]);

    // New Observation Warning Box
    const showNewObsWarning = () => {
        return (
            <div className={clsx(classes.newObsWarning, classes.blink)}>
                &#x26A0; {t('New observations have been made since last time this form was saved.')}
            </div>
        );
    };

    return (
        <>
            {newOBS && showNewObsWarning()}
            <div className={classes.section}>
                <Paper elevation={3} className={classes.category}>
                    {t('Could this be due to an infection?')}
                </Paper>

                {section1}
            </div>

            {(!checkIfSectionSelected(form.infection) && !checkIfSectionSelected(form.redFlag)) &&
                <InformationBox type="info">
                    {t('NONE SELECTED: LOW RISK OF SEPSIS')} <br/>
                    {t('USE STANDARD PROTOCOLS, REVIEW IF DETERIORATES')}
                </InformationBox>
            }

            <div className={classes.section}>
                <Paper elevation={3} className={classes.category}>
                    {t('Is any ONE red flag present?')}
                </Paper>

                {section2}
            </div>

            {!checkIfSectionSelected(form.redFlag) &&
                <>
                    <div className={classes.section}>
                        <Paper elevation={3} className={classes.category}>
                            {t('Any moderate risk criteria?')}
                        </Paper>
                        {section3}
                    </div>

                    {!checkIfSectionSelected(form.moderateRiskCriteria) &&
                        <>
                            <Divider className={classes.divider}/>

                            <InformationBox type="info">
                                {t('LOW RISK OF SEPSIS')} <br/>
                                {t('USE STANDARD PROTOCOLS, REVIEW IF DETERIORATES')}
                            </InformationBox>
                        </>
                    }
                </>
            }

            {checkIfSectionSelected(form.moderateRiskCriteria) &&
                <>
                    <Divider className={classes.divider}/>
                    <div className={classes.section}>
                        {section4}
                    </div>
                </>
            }
        </>
    );
};

export default AdultSepsisTool;